import React, { useState, useEffect, useContext, useCallback } from "react";
import {
	Container,
	Row,
	Col,
	Card,
	Form,
	ListGroup,
	Button,
	Badge,
} from "react-bootstrap";
import Select from "react-select";
import "bootstrap-icons/font/bootstrap-icons.css";
import axios from "axios";
import { FaStar, FaRegStar, FaStarHalfAlt } from "react-icons/fa"; // ✅ Import star icons
import { GoHeart, GoHeartFill } from "react-icons/go";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../context/appContext";

// ✅ Function to Render Stars Based on Rating
const renderStars = (rating = 0) => {
	const fullStars = Math.floor(rating);
	const halfStars = rating % 1 ? 1 : 0;
	const emptyStars = 5 - fullStars - halfStars;

	return (
		<>
			{Array(fullStars)
				.fill()
				.map((_, i) => (
					<FaStar key={`full-${i}`} style={{ color: "#ffcc00" }} />
				))}
			{halfStars > 0 && <FaStarHalfAlt style={{ color: "#ffcc00" }} />}
			{Array(emptyStars)
				.fill()
				.map((_, i) => (
					<FaRegStar key={`empty-${i}`} style={{ color: "#ffcc00" }} />
				))}
		</>
	);
};

// Replace this with your actual API Gateway URL
const API_URL =
	"https://lucvle8pqe.execute-api.us-east-1.amazonaws.com/v1/getBakeries-dev";
const FAV_BAKERIES_API_URL =
	"https://k0vygi49s4.execute-api.us-east-1.amazonaws.com/v2/postFavBakeries-dev";
// Replace with your actual Google API Key
const GOOGLE_API_KEY = "AIzaSyD5V5pktlC4weXq6ZWH0FavTWY-MEsewiw";

export default function BakeryFavs() {
	const { userSub, setGlobalLoading } = useContext(AppContext);

	const [page, setPage] = useState(0);
	const [address, setAddress] = useState("");
	const [zipCode, setZipCode] = useState("");
	const [dessert, setDessert] = useState("Cake");
	const [servings, setServings] = useState("");
	const [dietaryRestrictions, setDietaryRestrictions] = useState([]);
	const [otherNotes, setOtherNotes] = useState("");
	const [bakeries, setBakeries] = useState([]);
	const [suggestions, setSuggestions] = useState([]);
	const [zipToCityMap, setZipToCityMap] = useState({});
	const [lastEvaluatedKey, setLastEvaluatedKey] = useState("");
	const [favBakeries, setFavBakeries] = useState([]);

	const dietaryOptions = [
		{ value: "Gluten-Free", label: "Gluten-Free" },
		{ value: "Nut-Free", label: "Nut-Free" },
		{ value: "Dairy-Free", label: "Dairy-Free" },
		{ value: "Vegan", label: "Vegan" },
		{ value: "Kosher", label: "Kosher" },
		{ value: "Keto", label: "Keto" },
	];

	const navigate = useNavigate();

	const handleOrderClick = (bakeryName) => {
		navigate(`/${bakeryName}`, {
			state: {
				tiers: servings,
				dessertType: dessert,
				dietaryRestrictions: dietaryRestrictions,
			},
		});
	};

	const fetchCityForZip = async (zip) => {
		if (!zip) {
			return "Unknown location";
		}
		const cleanedZip = zip.replace(/\s/g, "").toUpperCase();

		if (zipToCityMap[cleanedZip]) return zipToCityMap[cleanedZip]; // ✅ use cleaned zip for caching

		// ✅ Hardcoded fallback for Canadian zips
		const hardcodedZips = {
			V4B2H1: "White Rock, BC, Canada",
			V4B4V7: "White Rock, BC, Canada",
		};

		if (hardcodedZips[cleanedZip]) {
			const fallback = hardcodedZips[cleanedZip];
			setZipToCityMap((prev) => ({ ...prev, [cleanedZip]: fallback }));
			return fallback;
		}

		try {
			const res = await axios.get(`https://api.zippopotam.us/us/${cleanedZip}`);
			const place = res.data.places?.[0];
			const cityState = `${place["place name"]}, ${place["state abbreviation"]}`;
			setZipToCityMap((prev) => ({ ...prev, [cleanedZip]: cityState }));
			return cityState;
		} catch (err) {
			console.error("Error fetching city for zip:", cleanedZip, err);
			return "Unknown location";
		}
	};

	const fetchBakeries = useCallback(
		async (zipFilter = "") => {
			try {
				setGlobalLoading(true);

        let url = `${API_URL}?fav=1`;
        if (userSub) {
					url = `${API_URL}?fav=1&userSub=${userSub}`;
				}

				const response = await axios.get(url);

				if (
					response.data &&
					response.data.bakeryStores &&
					Array.isArray(response.data.bakeryStores)
				) {
					let filteredBakeries = response.data.bakeryStores;

					// ✅ If a zip code is provided, filter manually in frontend
					if (zipFilter) {
						filteredBakeries = filteredBakeries.filter((bakery) => {
							// normalize data to ensure we are working with an array
							const zips = Array.isArray(bakery.zipCodes)
								? bakery.zipCodes
								: [bakery.zipCode];
							return zips.includes(zipFilter);
						});
					}

					filteredBakeries = filteredBakeries.filter(
						(bakery) => bakery.urlPathName !== "generic-quote-request",
					);

					const bakeryWithCity = await Promise.all(
						filteredBakeries.slice(0, 50).map(async (bakery) => {
							const firstZip = Array.isArray(bakery.zipCodes)
								? bakery.zipCodes[0]
								: bakery.zipCode;
							if (!firstZip) {
								return { ...bakery, city: "Unknown location" };
							}
							const city = await fetchCityForZip(firstZip);
							return { ...bakery, city };
						}),
					);
					setBakeries(bakeryWithCity);

					setLastEvaluatedKey(response.data?.lastEvaluatedKey?.bakeryName);
					setGlobalLoading(false);
				} else {
					console.error("❌ API returned an unexpected format:", response.data);
					setBakeries([]); // Set empty array if no valid data
					setGlobalLoading(false);
				}
			} catch (error) {
				console.error("❌ Error fetching bakeries:", error);
			}
		},
		[setGlobalLoading],
	);

	useEffect(() => {
		const loadCities = async () => {
			const map = {};

			for (const bakery of bakeries) {
				const zips = Array.isArray(bakery.zipCodes)
					? bakery.zipCodes
					: [bakery.zipCode];

				for (const zip of zips) {
					const splitZips = zip.split(","); // split on commas in case someone entered "77001,77002"
					for (const z of splitZips) {
						const cleaned = z.replace(/\s/g, "").toUpperCase();
						if (!map[cleaned]) {
							const city = await fetchCityForZip(cleaned);
							if (city) map[cleaned] = city;
						}
					}
				}
			}

			setZipToCityMap(map);
		};

		if (bakeries.length > 0) loadCities();
	}, [bakeries]);

	useEffect(() => {
		fetchBakeries();
	}, [fetchBakeries]);

	// Handle Search Button Click
	const handleSearch = () => {
		const extractedZip = address.match(/\d{5}/); // ✅ Looks for a 5-digit zip

		if (!address.trim()) {
			// ✅ If input is blank, show all bakeries
			setZipCode("");
			fetchBakeries();
		} else if (extractedZip) {
			// ✅ Valid zip found
			const zip = extractedZip[0];
			setZipCode(zip);
			fetchBakeries(zip);
		} else {
			// 🚫 Invalid input
			alert("Please enter a valid zip code.");
		}
	};

	// Function to fetch address suggestions from Google Places API (Fixed)
	const fetchPlaces = async (input) => {
		if (!input) {
			setSuggestions([]);
			return;
		}

		const endpoint = `https://places.googleapis.com/v1/places:autocomplete?key=${GOOGLE_API_KEY}`;

		const requestBody = {
			input: input,
			languageCode: "en",
			locationBias: {
				circle: {
					center: {
						latitude: 29.7604, // ✅ Houston, TX
						longitude: -95.3698,
					},
					radius: 50000, // 50km radius
				},
			},
		};

		try {
			const response = await fetch(endpoint, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(requestBody),
			});

			const data = await response.json();

			if (data.suggestions && data.suggestions.length > 0) {
				setSuggestions(
					data.suggestions.map((s) => ({
						description:
							s.placePrediction.structuredFormat.mainText.text +
							", " +
							s.placePrediction.structuredFormat.secondaryText.text,
						placeId: s.placePrediction.placeId,
					})),
				);
			} else {
				setSuggestions([]);
			}
		} catch (error) {
			console.error("🚨 Error fetching places:", error);
		}
	};

	const infiniteScroll = () => {
		const scrollReff = document.getElementById("scroll-ref");
		if (scrollReff) {
			const rect = scrollReff.getBoundingClientRect();
			if (rect.bottom < window.innerHeight + 10) {
				setPage((p) => p + 1);
			}
		}
	};

	useEffect(() => {
		window.addEventListener("scroll", infiniteScroll, true);
		return () => window.removeEventListener("scroll", infiniteScroll);
	}, []);

	useEffect(() => {
		if (page === 0) {
			return;
		}

		if (!lastEvaluatedKey) {
			return;
		}

		const fetchBakeriesOnScroll = async () => {
			try {
				setGlobalLoading(true);

        let url = `${API_URL}?fav=1&lastEvaluatedKey=${lastEvaluatedKey}`;
        if (userSub) {
					url = `${API_URL}?fav=1&lastEvaluatedKey=${lastEvaluatedKey}&userSub=${userSub}`;
				}

				const response = await axios.get(url);

				if (
					response.data &&
					response.data.bakeryStores &&
					Array.isArray(response.data.bakeryStores)
				) {
					const bakeryStores = response.data.bakeryStores;
					const tempBakeries = bakeries;
					const newBakeries = [...tempBakeries, ...bakeryStores];
					setBakeries(newBakeries);
					setLastEvaluatedKey(response.data?.lastEvaluatedKey?.bakeryName);
					setGlobalLoading(false);
				} else {
					console.error("❌ API returned an unexpected format:", response.data);
					setBakeries([]); // Set empty array if no valid data
					setGlobalLoading(false);
				}
			} catch (error) {
				console.error("❌ Error fetching bakeries:", error);
			}
		};

		fetchBakeriesOnScroll();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page]);

	useEffect(() => {
		const fn = async () => {
			try {
				let url = FAV_BAKERIES_API_URL;

				if (userSub) {
					url = `${FAV_BAKERIES_API_URL}?userSub=${userSub}`;
				}
				const fetchFavBakeriesResponse = await axios.get(url);

				if (
					fetchFavBakeriesResponse.data &&
					fetchFavBakeriesResponse.data.favBakeries &&
					Array.isArray(fetchFavBakeriesResponse.data.favBakeries)
				) {
					setFavBakeries(fetchFavBakeriesResponse.data.favBakeries);
				} else {
					console.error(
						"❌ API returned an unexpected format:",
						fetchFavBakeriesResponse.data,
					);
					setFavBakeries([]); // Set empty array if no valid data
				}
			} catch (error) {
				console.error("❌ Error fetching fav bakeries:", error);
			}
		};

		fn();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userSub]);

	const postFavBakeries = async (favBakeriesInput) => {
		try {
			await axios.post(FAV_BAKERIES_API_URL, {
				favBakeries: favBakeriesInput,
				...(userSub && { userSub }),
			});
		} catch (error) {
			setGlobalLoading(false);
			console.error("❌ Error adding fav bakeries:", error);
		}
	};

	return (
		<Container id="scroll-ref" className="page-container">
			{/* Header Section */}
			<Row className="header-section align-items-center">
				<Col md={8}>
					<h1 className="display-5 fw-bold">
						Find, Design, and Create Your <span className="gradient-text">Dream</span>{" "}
						Cake
					</h1>
					<p className="fs-5 mt-3">
						Support your local bakeries & home bakers while discovering the{" "}
						<span className="gradient-text">perfect cake</span> of your dreams!
					</p>
				</Col>
				<Col md={4} className="text-center">
					<img src="/faith.png" alt="Cupcake Mascot" className="header-image" />
				</Col>
			</Row>

			{/* Search Bar Section */}
			<div className="search-container">
				<Form className="d-flex flex-wrap gap-3 align-items-center w-100">
					{/* Address Field (Google API) */}
					<Form.Group className="search-field position-relative">
						<Form.Label className="fw-bold">Zip Code</Form.Label>
						<Form.Control
							type="text"
							value={address}
							onChange={(e) => {
								setAddress(e.target.value);
								fetchPlaces(e.target.value);
							}}
							placeholder="Enter zip code..."
						/>
						{suggestions.length > 0 && (
							<ListGroup className="shadow-sm position-absolute bg-white z-3">
								{suggestions.map((suggestion, index) => (
									<ListGroup.Item
										key={index}
										action
										onClick={() => {
											setAddress(suggestion.description);
											setSuggestions([]);
										}}>
										{suggestion.description}
									</ListGroup.Item>
								))}
							</ListGroup>
						)}
					</Form.Group>

					<Form.Group className="search-field">
						<Form.Label className="fw-bold">Dessert</Form.Label>
						<Form.Select value={dessert} onChange={(e) => setDessert(e.target.value)}>
							<option value="Cake">Cake</option>
							<option value="Cupcakes">Cupcakes</option>
							<option value="Cookies">Cookies</option>
						</Form.Select>
					</Form.Group>

					<Form.Group className="search-field">
						<Form.Label className="fw-bold"># Servings</Form.Label>
						<Form.Control
							type="number"
							value={servings}
							onChange={(e) => setServings(e.target.value)}
						/>
					</Form.Group>

					<Form.Group className="search-field">
						<Form.Label className="fw-bold">Dietary Restrictions</Form.Label>
						<Select
							isMulti
							options={dietaryOptions}
							value={dietaryRestrictions}
							onChange={setDietaryRestrictions}
						/>
					</Form.Group>

					{/* Other Notes Field */}
					{/* <Form.Group className="search-field">
            <Form.Label className="fw-bold">Other Notes</Form.Label>
            <Form.Control
              type="text"
              placeholder="Any additional details..."
              value={otherNotes}
              onChange={(e) => setOtherNotes(e.target.value)}
            />
          </Form.Group> */}

					<Form.Group className="search-btn-container">
						<Button className="search-btn" onClick={handleSearch}>
							<i className="bi bi-search"></i>
						</Button>
					</Form.Group>
				</Form>
			</div>

			{/* Favorite Bakeries */}
			<h2 className="fw-semibold mt-5">Favorite Bakeries</h2>
			<Row className="mt-4">
				{bakeries.length > 0 ? (
					bakeries.map((bakery, index) => (
						<Col md={3} className="mb-4" key={index}>
							<Card className="bakery-card shadow-sm">
								{/* ✅ Ensuring image loads properly */}
								<Card.Img
									variant="top"
									src={bakery.bakeryLogoUrl || "/default-bakery.jpg"} // ✅ Uses API image or fallback
									alt={bakery.bakeryName || "Bakery"}
									style={{
										height: "200px",
										objectFit: "cover",
										borderTopLeftRadius: "15px",
										borderTopRightRadius: "15px",
									}}
								/>
								<Card.Body>
									{/* ✅ Display Bakery Name */}
									<h5 style={{ color: "#ff6f91" }}>
										{bakery.bakeryName || "Unnamed Bakery"}
									</h5>

									{/* ✅ Display Rating with Stars */}
									<div className="d-flex align-items-center justify-content-between mb-2">
										<div className="d-flex align-items-center">
											<div className="me-2">{renderStars(bakery.rating)}</div>
											<small style={{ color: "#333333" }}>
												({bakery.reviews ? `${bakery.reviews} reviews` : "No reviews"})
											</small>
										</div>
										{favBakeries.includes(bakery.bakeryName) ? (
											<GoHeartFill
												style={{ color: "#ff6f91", fontSize: "24px", cursor: "pointer" }}
												onClick={() => {
													const newFavBakeries = favBakeries.filter(
														(p) => p !== bakery.bakeryName,
													);
													setFavBakeries(newFavBakeries);
													postFavBakeries(newFavBakeries);
												}}
											/>
										) : (
											<GoHeart
												style={{ color: "#ff6f91", fontSize: "24px", cursor: "pointer" }}
												onClick={() => {
													const newFavBakeries = [...favBakeries, bakery.bakeryName];
													setFavBakeries(newFavBakeries);
													postFavBakeries(newFavBakeries);
												}}
											/>
										)}
									</div>

									{bakery.city && (
										<div
											className="bakery-location"
											style={{ fontSize: "14px", color: "#555" }}>
											{bakery.city}
										</div>
									)}

									{/* ✅ Price Display */}
									{bakery.startingPrice && (
										<p className="mb-2">
											Starting at{" "}
											<Badge bg="light" text="dark">
												${bakery.startingPrice}/serving
											</Badge>
										</p>
									)}

									{/* ✅ Order Button */}
									<Button
										variant="primary"
										onClick={() => handleOrderClick(bakery.urlPathName)}
										style={{
											backgroundColor: "#fea2c2",
											borderColor: "#ff6f91",
											width: "100%",
											fontSize: "16px",
										}}>
										Request Quote
									</Button>
								</Card.Body>
							</Card>
						</Col>
					))
				) : (
					<div className="text-center mt-5 pb-5">
						<h4
							style={{
								color: "#FEA2C2",
							}}>
							No bakeries match this criteria
						</h4>
						<p className="mb-2">But don’t worry — we’ve still got you covered!</p>
						<p className="mb-3">
							Submit a generic quote request and we’ll match you with a bakery ASAP
							(generally within 2 days).
						</p>

						<Button
							onClick={() => navigate("/genericquoterequest")}
							style={{
								backgroundColor: "#FEA2C2",
								border: "none",
								color: "white",
								fontWeight: "bold",
								padding: "12px 24px",
								fontSize: "1.1rem",
								borderRadius: "8px",
								boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
								transition: "all 0.3s ease-in-out",
							}}>
							Submit a Generic Quote Request
						</Button>
					</div>
				)}
			</Row>
		</Container>
	);
}
